import ErrorNotifier from 'app/scripts/services/error-notifier';
import { getSpotId } from 'app/scripts/services/app-state';
import { TDispatch, TGetState } from 'state';
import { isEmail } from 'validator';
import { userParser } from '../parsers/user-parser';
import { findUserByUsername, findUserByEmail } from '../../api/find-user';
import { ModerationSearchType } from '../types';
import { searchModerationItemsSuccess, searchModerationItemsFailure } from '.';

type Payload = {
  type: ModerationSearchType;
  value: string;

  // userId will be available only on USERNAME search,
  // since the client can select any user suggestion from the list.
  userId?: string;
};

const searchModeration = ({ type, value, userId }: Payload) => {
  return async (dispatch: TDispatch): Promise<void> => {
    const spotId: string = getSpotId();

    try {
      switch (type) {
        case ModerationSearchType.USERNAME:
          dispatch(searchByUserName(spotId, value, userId));
          break;
        case ModerationSearchType.MESSAGE_ID:
          dispatch(searchByMessageID(spotId, value));
          break;
        case ModerationSearchType.EMAIL:
          dispatch(searchByEmail(spotId, value));
          break;
        case ModerationSearchType.TEXT:
          dispatch(searchByText(value));
          break;
        default:
          throw new Error(`Invalid Search Type: ${type}`);
      }
    } catch (error) {
      dispatch(
        searchFailed({
          searchType: type,
          searchValue: value,
          error
        })
      );
    }
  };
};

const searchByEmail = (spotId: string, email: string) => {
  return async (dispatch: TDispatch): Promise<void> => {
    const isValidEmail: boolean = isEmail(email);

    if (!isValidEmail) {
      dispatch(
        searchFailed({
          searchType: ModerationSearchType.EMAIL,
          searchValue: email,
          error: new Error(`Invalid Email: ${email}`)
        })
      );
      return;
    }

    try {
      const { suggestions } = await findUserByEmail(email);

      const userSuggestions = userParser(suggestions);
      const hasNoSuggestions: boolean = userSuggestions.length < 1;

      if (hasNoSuggestions) {
        dispatch(
          searchFailed({
            searchType: ModerationSearchType.EMAIL,
            searchValue: email,
            error: `Couldn't find email: ${email}`
          })
        );
        return;
      }

      const userId = userSuggestions[0].userId;

      if (userId) {
        dispatch(
          searchModerationItemsSuccess({
            searchType: ModerationSearchType.EMAIL,
            userId,
            searchValue: email
          })
        );
      } else {
        dispatch(
          searchFailed({
            searchType: ModerationSearchType.EMAIL,
            searchValue: email,
            error: `Suggested user is missing id`
          })
        );
      }
    } catch (error) {
      dispatch(
        searchFailed({
          searchType: ModerationSearchType.EMAIL,
          searchValue: email,
          error: 'Failed to find user by email'
        })
      );
    }
  };
};

const searchByUserName = (spotId: string, username: string, userId?: string) => {
  return async (dispatch: TDispatch): Promise<void> => {
    let searchUserId: string | undefined = userId;

    if (!searchUserId) {
      try {
        const { suggestions } = await findUserByUsername(username);

        const userSuggestions = userParser(suggestions);
        const hasNoSuggestions: boolean = userSuggestions.length < 1;

        if (hasNoSuggestions) {
          dispatch(
            searchFailed({
              searchType: ModerationSearchType.USERNAME,
              searchValue: username,
              error: `Couldn't find email: ${username}`
            })
          );
          return;
        }

        searchUserId = userSuggestions[0].userId;
      } catch (error) {
        dispatch(
          searchFailed({
            searchType: ModerationSearchType.USERNAME,
            searchValue: username,
            error: 'Failed to search by username'
          })
        );
      }
    }

    if (searchUserId) {
      dispatch(
        searchModerationItemsSuccess({
          searchType: ModerationSearchType.USERNAME,
          userId: searchUserId,
          searchValue: username
        })
      );
    } else {
      dispatch(
        searchFailed({
          searchType: ModerationSearchType.USERNAME,
          searchValue: username,
          error: `User is missing id`
        })
      );
    }
  };
};

const searchByText = (searchValue: string) => {
  return async (dispatch: TDispatch): Promise<void> => {
    dispatch(
      searchModerationItemsSuccess({
        searchType: ModerationSearchType.TEXT,
        searchValue
      })
    );
  };
};
const searchByMessageID = (spotId: string, messageID: string) => {
  return async (dispatch: TDispatch): Promise<void> => {
    dispatch(
      searchModerationItemsSuccess({
        searchType: ModerationSearchType.MESSAGE_ID,
        searchValue: messageID
      })
    );
  };
};

const searchFailed = ({
  searchType,
  searchValue,
  error
}: {
  searchType: ModerationSearchType;
  searchValue: string;
  error: string | Error;
}) => {
  return async (dispatch: TDispatch): Promise<void> => {
    console.error(error);
    ErrorNotifier.notifyError(error, { searchType, searchValue });

    dispatch(
      searchModerationItemsFailure({
        searchType,
        searchValue,
        error
      })
    );
  };
};

export default searchModeration;
